import moment from "moment"
import { useEffect, useState } from "react"
import { Modal } from "react-bootstrap"
import { useFetchNearbyShips } from "../hooks/useFetchNearbyShips"
import { useFetchVesselPositions4 } from "../hooks/useFetchVesselPositions"
import { VesselStay } from "../hooks/useFetchVesselStays"
import { VesselDetails } from "../hooks/usePrevetAPI"
import { VesselSegmentInputSelection } from "./PrevetMain"
import { PrevetModalInfo, TimeWindow } from "./PrevetModalInfo"
import { PrevetModalMap } from "./PrevetModalMap"

interface PrevetVesselTradingModalProps {
  vesselDetails: VesselDetails
  vesselStays: VesselStay[]
  chosenSegment: VesselSegmentInputSelection
  isTradingModalShown: boolean
  closeTradingModal: () => void
  dateForCenterMarker: string | undefined
  chartLadenStatus?: boolean
}

const defaultTimeWindow = { label: "6 Months", value: 6 }

// Must stay in sync with the options offered by PrevetModalInfo.
const timeWindowOptions: TimeWindow[] = [
  { label: "1 Month", value: 1 },
  { label: "3 Months", value: 3 },
  { label: "6 Months", value: 6 },
  { label: "1 year", value: 12 },
  { label: "2 years", value: 24 },
]

/**
 * Pick the smallest trading pattern window that still contains the clicked date.
 * The API builds its range as `today - months`, so the comparison below mirrors
 * that exactly - otherwise the clicked position is simply not in the response and
 * the map cannot centre on it.
 */
function calculateTimeWindow(date: string | undefined): TimeWindow {
  if (!date) {
    return defaultTimeWindow
  }
  const start = moment(date, "YYYY-MM-DD").startOf("day")
  if (!start.isValid()) {
    return defaultTimeWindow
  }
  const today = moment().startOf("day")

  const match = timeWindowOptions.find(
    (option) => !start.isBefore(today.clone().subtract(option.value, "months"))
  )

  return match ?? timeWindowOptions[timeWindowOptions.length - 1]
}

export function PrevetVesselTradingModal({
  chosenSegment,
  isTradingModalShown,
  closeTradingModal,
  dateForCenterMarker,
  chartLadenStatus,
  vesselDetails,
  vesselStays,
}: PrevetVesselTradingModalProps) {
  const [timeWindow, setTimeWindow] = useState<TimeWindow>(() =>
    calculateTimeWindow(dateForCenterMarker)
  )
  const { vesselPositions } = useFetchVesselPositions4({
    imo: vesselDetails.imo,
    segment: chosenSegment.value,
    months: timeWindow.value,
  })

  const [showTradingPatternMarkers, setShowTradingPatternMarkers] =
    useState(true)

  const [showPortStayMarkers, setShowPortStayMarkers] = useState(true)

  const effectiveCenterDate = dateForCenterMarker

  const { nearbyShipsData } = useFetchNearbyShips({
    imo: vesselDetails.imo,
    date: dateForCenterMarker ?? null,
  })

  useEffect(() => {
    // This useEffect runs when the modal is opened from user selecting
    // a point on the SpeedGraph.
    const tw = calculateTimeWindow(dateForCenterMarker)
    setTimeWindow(tw)
  }, [dateForCenterMarker])

  return (
    <Modal
      className="PrevetVesselTradingModal"
      size="xl"
      show={isTradingModalShown}
    >
      <div className="modal-header">
        <h5 className="modal-title mt-0" id="OverviewModalLabel">
          Trade Map
        </h5>
        <button
          onClick={() => {
            closeTradingModal()
          }}
          type="button"
          className="btn-close"
          data-dismiss="modal"
          aria-label="Close"
        />
      </div>
      {vesselPositions ? (
        <>
          <div className="PrevetVesselTradingModal_Info">
            <PrevetModalInfo
              chosenSegment={chosenSegment}
              vesselDetails={vesselDetails}
              timeWindow={timeWindow}
              setTimeWindow={({ label, value }: TimeWindow) => {
                setTimeWindow({ label, value })
              }}
              showTradingPatternMarkers={showTradingPatternMarkers}
              setShowTradingPatternMarkers={setShowTradingPatternMarkers}
              showPortStayMarkers={showPortStayMarkers}
              setShowPortStayMarkers={setShowPortStayMarkers}
            />
          </div>
          <div className="PrevetVesselTradingModal_Row">
            <div className="PrevetVesselTradingModal_Map">
              <PrevetModalMap
                vesselStays={vesselStays}
                vesselPositions={vesselPositions}
                showPortStayMarkers={showPortStayMarkers}
                showTradingPatternMarkers={showTradingPatternMarkers}
                dateForCenterMarker={effectiveCenterDate}
                timeWindow={timeWindow}
                nearbyShipsData={nearbyShipsData}
                segmentName={chosenSegment.value}
                chartLadenStatus={chartLadenStatus}
              />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="w-100 text-center py-5">Loading trade map...</div>
        </>
      )}
    </Modal>
  )
}
